import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom'; // Navigation hook for React Router
import { jwtDecode } from 'jwt-decode';
import './ClientBuild.css'; // Import the CSS file for styles

const ClientBuild = () => {
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [userResponse, setUserResponse] = useState('');
  const [conversation, setConversation] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(''); // Add this line at the start of your component
  const [emailSent, setEmailSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Function to check if the token has expired
  const checkTokenExpiration = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      if (decodedToken.exp < Date.now() / 1000) {
        handleLogout();
      }
    } else {
      handleLogout();
    }
  }, [navigate]);

  // Logout function
  const handleLogout = useCallback(() => {
    if (window.confirm('Are you sure you want to log out?')) {
      localStorage.removeItem('token');
      navigate('/login');
    }
  }, [navigate]);

  // Fetch questions on mount
  useEffect(() => {
    checkTokenExpiration();
    const fetchQuestions = async () => {
      const userId = localStorage.getItem('userId');
      if (userId) {
        try {
          const { data } = await axios.get(`${apiUrl}/api/users/${userId}/questions`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          setQuestions(data);
          if (data.length > 0) {
            setCurrentQuestion(data[0].questionText);
          }
        } catch (error) {
          console.error('Failed to load questions:', error);
          setErrorMessage('Failed to load questions');
        }
      }
    };
    fetchQuestions();

    return () => {
      clearInterval(checkTokenExpiration);
    };
  }, [apiUrl, checkTokenExpiration]);

  // Handle user response submission
  const handleUserResponse = async () => {
    const nextIndex = currentQuestionIndex + 1;
    const updatedConversation = [...conversation, { question: currentQuestion, response: userResponse }];
  
    // Immediately update conversation state to include the latest response
    setConversation(updatedConversation);
  
    if (nextIndex < questions.length) {
      // Not the last question, so update question state as usual
      setCurrentQuestion(questions[nextIndex].questionText);
      setCurrentQuestionIndex(nextIndex);
      setUserResponse(''); // Clear the response for the next question
    } else {
      // Delay the finish session to ensure state updates are rendered
      setTimeout(() => handleFinishSession(updatedConversation), 0);
    }
  };

// Finish the session and generate summary
const handleFinishSession = async (finalConversation) => {
  setLoading(true);
  const userId = localStorage.getItem('userId'); // Fetch userId from localStorage
  if (!userId) {
    setErrorMessage('User ID not found. Please log in again.');
    setLoading(false);
    return;
  }

  try {
    // Fetch custom survey criteria
    const criteriaResponse = await axios.get(`${apiUrl}/api/users/${userId}/survey-criteria`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    const surveyCriteria = criteriaResponse.data.customSurveyCriteria; // Ensure this matches the actual key returned

    // Now send the final conversation along with the fetched survey criteria to generate the summary
    const response = await axios.post(`${apiUrl}/api/openai/questions/summary`, {
      responses: finalConversation.map(conv => `${conv.question} ${conv.response}`),
      surveyCriteria // Include the criteria in the request
    });

    const generatedSummary = response.data.summary;

    // Automatically call handleSendEmail after generating the summary
    await handleSendEmail(userId, generatedSummary);

    setSummary(generatedSummary);
    setShowSummary(false); // Ensure the summary is not displayed on the screen

    // Display a thank you message and disable the "Submit Response" button
    setMessage('Thank you for completing the survey. Your coach will reach out to you shortly.');
  } catch (error) {
    console.error('Error generating the summary or fetching criteria:', error);
    setErrorMessage('Failed to generate the summary or fetch criteria');
  } finally {
    setLoading(false);
  }
};

// Modify handleSendEmail to accept userId and summary as parameters
const handleSendEmail = async (userId, summary) => {
  try {
    setLoading(true);
    // Send userId and summary to the backend
    await axios.post(`${apiUrl}/api/openai/send-summary-email`, {
      userId,
      summary
    });
    alert('Survey sent successfully!');
  } catch (error) {
    console.error('Failed to send survey:', error);
    setErrorMessage('Failed to send the survey');
  } finally {
    setLoading(false);
  }
};

    // Navigation back function
    const handleGoBack = () => {
      navigate('/dashboard');
    };

  return (
    <div className="client-build-container">
    <h1>Interactive Survey Session</h1>
    {errorMessage && <p className="error-message">{errorMessage}</p>}
    {conversation.map((conv, index) => (
      <div key={index} className="conversation-item">
        <strong>{conv.question}</strong>
        <p>{conv.response}</p>
      </div>
    ))}
    {!showSummary && currentQuestion && (
      <div className="question-section">
        <p className="current-question">{currentQuestion}</p>
        <textarea
          value={userResponse}
          onChange={(e) => setUserResponse(e.target.value)}
          rows="3"
          placeholder="Type your answer here..."
        />
        <div className="action-buttons">
          <button onClick={handleUserResponse} disabled={loading || !userResponse.trim() || message}>
            {loading ? 'Processing...' : 'Submit Response'}
          </button>
        </div>
      </div>
    )}
    {message && <p className="thank-you-message">{message}</p>}
    <div className="navigation-buttons">
      <button onClick={handleGoBack}>Back to Dashboard</button>
    </div>
  </div>
);
};

export default ClientBuild;
